/**
 * LoadoutScreen — Full-screen overlay shown after the username/mode prompt.
 * Lets the player pick 3 starter drones from the Common-tier roster (click
 * each slot to cycle through the available types). Submits the chosen
 * loadout via `room.join({ loadout: [...] })`.
 *
 * Visual aesthetic mirrors `UsernamePrompt` (animated gradient border, cyan
 * accents, monospace tech-y typography).
 */

import { DroneType, DRONE_STATS, Tier } from '../../shared/types.js';
import { LOADOUT_SLOTS, DEFAULT_LOADOUT } from '../../shared/constants.js';

interface DroneOption {
  type: DroneType;
  name: string;
  abbr: string;
}

/** Build the picker list once: every Common-tier drone type, in enum order. */
function buildOptions(): DroneOption[] {
  const labels: Record<DroneType, { name: string; abbr: string }> = {
    [DroneType.Striker]:     { name: 'Striker',     abbr: 'STR' },
    [DroneType.Sniper]:      { name: 'Sniper',      abbr: 'SNP' },
    [DroneType.Healer]:      { name: 'Healer',      abbr: 'HLR' },
    [DroneType.Interceptor]: { name: 'Interceptor', abbr: 'INT' },
    [DroneType.Support]:     { name: 'Support',     abbr: 'SUP' },
    [DroneType.Guardian]:    { name: 'Guardian',    abbr: 'GRD' },
  };
  const out: DroneOption[] = [];
  // Iterate over all enum values and keep only Common-tier ones to honor the
  // balance constraint. Today only Striker/Healer qualify; future Common
  // additions show up automatically.
  for (const k of Object.keys(DRONE_STATS)) {
    const t = Number(k) as DroneType;
    if (DRONE_STATS[t].tier !== Tier.Common) continue;
    const lbl = labels[t];
    if (lbl) out.push({ type: t, name: lbl.name, abbr: lbl.abbr });
  }
  return out;
}

export class LoadoutScreen {
  private overlay: HTMLElement;
  private slotEls: HTMLElement[] = [];
  private options: DroneOption[] = buildOptions();
  private selection: number[] = [...DEFAULT_LOADOUT];
  private deployBtn: HTMLButtonElement;
  private onSubmitCb: ((loadout: number[]) => void) | null = null;

  constructor(private parentElement: HTMLElement) {
    this.overlay = document.createElement('div');
    this.overlay.className = 'loadout-overlay hidden';

    const card = document.createElement('div');
    card.className = 'loadout-card';

    const title = document.createElement('div');
    title.className = 'loadout-title';
    title.textContent = 'CHOOSE LOADOUT';
    card.appendChild(title);

    const subtitle = document.createElement('div');
    subtitle.className = 'loadout-subtitle';
    subtitle.textContent = 'Pick 3 starter drones (Common tier)';
    card.appendChild(subtitle);

    const slotsRow = document.createElement('div');
    slotsRow.className = 'loadout-slots';
    for (let i = 0; i < LOADOUT_SLOTS; i++) {
      const slot = this.buildSlot(i);
      slotsRow.appendChild(slot);
      this.slotEls.push(slot);
    }
    card.appendChild(slotsRow);

    this.deployBtn = document.createElement('button');
    this.deployBtn.className = 'loadout-deploy';
    this.deployBtn.textContent = 'DEPLOY';
    this.deployBtn.addEventListener('click', () => this.handleSubmit());
    card.appendChild(this.deployBtn);

    const hint = document.createElement('div');
    hint.className = 'loadout-hint';
    hint.textContent = 'Click each slot to cycle. Default: Striker / Striker / Healer.';
    card.appendChild(hint);

    this.overlay.appendChild(card);
    this.parentElement.appendChild(this.overlay);

    this.refresh();
  }

  /** Display the prompt; `onSubmit` fires when the player clicks DEPLOY. */
  show(onSubmit: (loadout: number[]) => void, presetLoadout?: number[]): void {
    this.onSubmitCb = onSubmit;
    if (presetLoadout && presetLoadout.length === LOADOUT_SLOTS) {
      this.selection = [...presetLoadout];
    }
    this.overlay.classList.remove('hidden', 'hiding');
    this.refresh();
  }

  /** Fade out and hide. */
  hide(): void {
    this.overlay.classList.add('hiding');
    const onEnd = () => {
      this.overlay.classList.add('hidden');
      this.overlay.classList.remove('hiding');
      this.overlay.removeEventListener('animationend', onEnd);
    };
    this.overlay.addEventListener('animationend', onEnd);
  }

  private buildSlot(index: number): HTMLElement {
    const slot = document.createElement('button');
    slot.type = 'button';
    slot.className = 'loadout-slot';
    slot.dataset.slot = String(index);

    const slotLabel = document.createElement('div');
    slotLabel.className = 'loadout-slot-label';
    slotLabel.textContent = `SLOT ${index + 1}`;
    slot.appendChild(slotLabel);

    const abbr = document.createElement('div');
    abbr.className = 'loadout-slot-abbr';
    slot.appendChild(abbr);

    const name = document.createElement('div');
    name.className = 'loadout-slot-name';
    slot.appendChild(name);

    const tier = document.createElement('div');
    tier.className = 'loadout-slot-tier';
    tier.textContent = 'COMMON';
    slot.appendChild(tier);

    const stats = document.createElement('div');
    stats.className = 'loadout-slot-stats';
    slot.appendChild(stats);

    const cycleHint = document.createElement('div');
    cycleHint.className = 'loadout-slot-hint';
    cycleHint.textContent = 'click to cycle';
    slot.appendChild(cycleHint);

    slot.addEventListener('click', () => this.cycleSlot(index));
    return slot;
  }

  private cycleSlot(index: number): void {
    const current = this.selection[index];
    const idx = this.options.findIndex((o) => o.type === current);
    const next = this.options[(idx + 1) % this.options.length];
    this.selection[index] = next.type;
    this.refresh();
  }

  /** Repaint every slot from `this.selection`. Cheap — runs only on click. */
  private refresh(): void {
    for (let i = 0; i < this.slotEls.length; i++) {
      const slot = this.slotEls[i];
      const type = this.selection[i] as DroneType;
      const opt = this.options.find((o) => o.type === type) ?? this.options[0];
      const stats = DRONE_STATS[type];
      const abbrEl = slot.querySelector('.loadout-slot-abbr') as HTMLElement;
      const nameEl = slot.querySelector('.loadout-slot-name') as HTMLElement;
      const statsEl = slot.querySelector('.loadout-slot-stats') as HTMLElement;
      abbrEl.textContent = opt.abbr;
      nameEl.textContent = opt.name.toUpperCase();
      statsEl.innerHTML =
        `<span>HP <b>${stats.health}</b></span>` +
        `<span>SPD <b>${stats.speed}</b></span>` +
        `<span>DMG <b>${stats.damage}</b></span>` +
        `<span>RNG <b>${stats.projectileRange || stats.meleeRadius}</b></span>`;
    }
  }

  private handleSubmit(): void {
    this.onSubmitCb?.([...this.selection]);
  }
}
